/**
 * Flutter 앱과의 통신 브릿지
 *
 * WebView 캐시 삭제 시 자동 로그인을 위한 Custom Token 관리
 * - 로그인 성공 시: saveCustomToken()으로 앱에 토큰 저장
 * - 앱 진입 시: requestCustomToken()으로 저장된 토큰 요청
 * - 로그아웃 시: clearCustomToken()으로 저장된 토큰 삭제
 */

import { computed } from 'vue'

/**
 * JWT에서 UID 추출 (서명 검증 없이 payload만)
 * Custom Token은 이미 만료되었으므로 UID 추출용으로만 사용
 */
export function extractUidFromToken(token: string): string | null {
  try {
    const payload = token.split('.')[1]
    const decoded = JSON.parse(atob(payload))
    return decoded.uid || null
  } catch {
    return null
  }
}

export function useFlutterBridge() {
  /**
   * Flutter 앱 환경인지 확인
   * - URL에 app=flutter 파라미터가 있거나
   * - window.flutter_inappwebview 객체가 존재하면 Flutter 앱
   */
  const isFlutterApp = computed(() => {
    if (import.meta.client) {
      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get('app') === 'flutter' || !!window.flutter_inappwebview
    }
    return false
  })

  /**
   * Flutter 앱에 메시지 전송
   */
  async function callFlutter(action: string, data?: Record<string, any>) {
    if (!import.meta.client || !window.flutter_inappwebview?.callHandler) {
      return null
    }
    return window.flutter_inappwebview.callHandler('FlutterChannel', { action, ...data })
  }

  /**
   * Custom Token을 앱에 저장
   * 로그인 성공 시 호출
   */
  async function saveCustomToken(token: string) {
    return callFlutter('saveCustomToken', { token })
  }

  /**
   * 앱에서 저장된 Custom Token 요청
   * 앱 진입 시 자동 로그인을 위해 호출
   */
  function requestCustomToken(): Promise<string | null> {
    return new Promise((resolve) => {
      if (!import.meta.client) return resolve(null)

      const handler = (event: MessageEvent) => {
        if (event.data?.type === 'CUSTOM_TOKEN_RESPONSE') {
          window.removeEventListener('message', handler)
          resolve(event.data.token || null)
        }
      }
      window.addEventListener('message', handler)
      callFlutter('getCustomToken')

      // 3초 타임아웃
      setTimeout(() => {
        window.removeEventListener('message', handler)
        resolve(null)
      }, 3000)
    })
  }

  /**
   * 앱에서 저장된 Custom Token 삭제
   * 로그아웃 시 호출
   */
  async function clearCustomToken() {
    return callFlutter('clearCustomToken')
  }

  /**
   * 웹 준비 완료 신호 전송
   */
  function sendWebReady() {
    return callFlutter('webReady')
  }

  /**
   * 앱 환경 정보 요청
   * 앱에서 OS, 앱 버전 등의 정보를 받아옴
   */
  function requestAppEnvironment(): Promise<AppEnvironment | null> {
    return new Promise((resolve) => {
      if (!import.meta.client) return resolve(null)

      const handler = (event: MessageEvent) => {
        if (event.data?.type === 'APP_ENVIRONMENT_RESPONSE') {
          window.removeEventListener('message', handler)
          resolve(event.data.data || null)
        }
      }
      window.addEventListener('message', handler)
      callFlutter('getAppEnvironment')

      // 3초 타임아웃
      setTimeout(() => {
        window.removeEventListener('message', handler)
        resolve(null)
      }, 3000)
    })
  }

  return {
    isFlutterApp,
    saveCustomToken,
    requestCustomToken,
    clearCustomToken,
    sendWebReady,
    requestAppEnvironment,
    extractUidFromToken,
  }
}

/**
 * 앱 환경 정보 타입
 */
export interface AppEnvironment {
  platform: 'android' | 'ios' | 'unknown'
  osVersion: string
  appVersion: string
  buildNumber: string
  model: string
  brand?: string
}
